import React, { useState, useCallback } from 'react';
import { fmtDate, fmtMonthYear } from '@shared/formatDate';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Slider,
  Switch,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Pagination,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  VolunteerActivism as DonationIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  LocalPostOffice as PostageIcon,
  AdsClick as AdClickIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Types
interface DonationPreference {
  PreferenceId: string;
  CharityCredentialId: string;
  CharityUsername: string;
  CharityName: string;
  CharityType?: string;
  DonationPercentage: number;
  DonatePostageRevenue: boolean;
  DonateAdClickRevenue: boolean;
  CreatedDate: string;
  NextProcessingDate: string;
}

interface DonationPreferencesResponse {
  hasPreference: boolean;
  preference?: DonationPreference;
}

interface Charity {
  CredentialId: string;
  Username: string;
  OrganizationName: string;
  CharityType?: string;
  TaxIdNumber?: string;
  City?: string;
  State?: string;
  CountryCode?: string;
  VerificationDate?: string;
}

interface VerifiedCharitiesResponse {
  charities: Charity[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface DonationRecord {
  DonationId: string;
  CharityName: string;
  CharityUsername: string;
  Amount: number;
  PostageAmount: number;
  AdClickAmount: number;
  Percentage: number;
  PeriodStart: string;
  PeriodEnd: string;
  Status: string;
  ProcessedDate: string | null;
}

interface DonationHistoryResponse {
  donations: DonationRecord[];
  totalDonated: number;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const CHARITY_THEME = {
  primary: '#2e7d32',
  light: '#4caf50',
  gradient: 'linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)',
};

export default function DonationSettings() {
  const queryClient = useQueryClient();
  const [charitySearch, setCharitySearch] = useState('');
  const [selectedCharity, setSelectedCharity] = useState<Charity | null>(null);
  const [donationPercentage, setDonationPercentage] = useState(10);
  const [donatePostageRevenue, setDonatePostageRevenue] = useState(true);
  const [donateAdClickRevenue, setDonateAdClickRevenue] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pageSize = 20;

  // Fetch donation preferences
  const { data: preferencesData, isLoading: preferencesLoading } = useQuery({
    queryKey: ['donationPreferences'],
    queryFn: () => api.get<DonationPreferencesResponse>('/api/donations/preferences'),
  });

  // Sync form when preferences load
  React.useEffect(() => {
    if (preferencesData?.hasPreference && preferencesData.preference) {
      const p = preferencesData.preference;
      setDonationPercentage(p.DonationPercentage);
      setDonatePostageRevenue(p.DonatePostageRevenue);
      setDonateAdClickRevenue(p.DonateAdClickRevenue);
    }
  }, [preferencesData]);

  // Debounced charity search for autocomplete
  const [debouncedCharitySearch, setDebouncedCharitySearch] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedCharitySearch(charitySearch.trim()), 400);
    return () => clearTimeout(timer);
  }, [charitySearch]);

  const { data: charitiesData, isLoading: charitiesLoading } = useQuery({
    queryKey: ['verifiedCharities', debouncedCharitySearch],
    queryFn: () =>
      api.get<VerifiedCharitiesResponse>(
        `/api/charities/verified?search=${encodeURIComponent(debouncedCharitySearch || '')}&page=1&pageSize=20&country=US`
      ),
    enabled: true,
  });

  const charities = charitiesData?.charities ?? [];

  // Fetch donation history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['donationHistory', historyPage],
    queryFn: () =>
      api.get<DonationHistoryResponse>(
        `/api/donations/history?page=${historyPage}&pageSize=${pageSize}`
      ),
  });

  // Save preference mutation
  const saveMutation = useMutation({
    mutationFn: (body: {
      CharityUsername: string;
      DonationPercentage: number;
      DonatePostageRevenue: boolean;
      DonateAdClickRevenue: boolean;
    }) => api.post('/api/donations/preferences', body),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['donationPreferences'] });
      queryClient.invalidateQueries({ queryKey: ['donationHistory'] });
      setSuccessMessage(data?.message || 'Donation preference saved.');
      setErrorMessage(null);
      setSelectedCharity(null);
      setCharitySearch('');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.error || 'Failed to save donation preference.');
      setSuccessMessage(null);
    },
  });

  // Delete preference mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.delete('/api/donations/preferences'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donationPreferences'] });
      queryClient.invalidateQueries({ queryKey: ['donationHistory'] });
      setSuccessMessage('Donation preference removed.');
      setErrorMessage(null);
      setSelectedCharity(null);
      setCharitySearch('');
      setDonationPercentage(10);
      setDonatePostageRevenue(true);
      setDonateAdClickRevenue(true);
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.error || 'Failed to remove donation preference.');
      setSuccessMessage(null);
    },
  });

  const handleSave = useCallback(() => {
    if (!selectedCharity && !preferencesData?.hasPreference) {
      setErrorMessage('Please select a charity.');
      return;
    }
    const charityUsername = selectedCharity?.Username ?? preferencesData?.preference?.CharityUsername;
    if (!charityUsername) {
      setErrorMessage('Please select a charity.');
      return;
    }
    if (!donatePostageRevenue && !donateAdClickRevenue) {
      setErrorMessage('Please select at least one donation source (postage or ad revenue).');
      return;
    }
    setErrorMessage(null);
    saveMutation.mutate({
      CharityUsername: charityUsername,
      DonationPercentage: donationPercentage,
      DonatePostageRevenue: donatePostageRevenue,
      DonateAdClickRevenue: donateAdClickRevenue,
    });
  }, [
    selectedCharity,
    preferencesData,
    donationPercentage,
    donatePostageRevenue,
    donateAdClickRevenue,
    saveMutation,
  ]);

  const handleCancel = useCallback(() => {
    setSelectedCharity(null);
    setCharitySearch('');
    if (preferencesData?.hasPreference && preferencesData.preference) {
      const p = preferencesData.preference;
      setDonationPercentage(p.DonationPercentage);
      setDonatePostageRevenue(p.DonatePostageRevenue);
      setDonateAdClickRevenue(p.DonateAdClickRevenue);
    } else {
      setDonationPercentage(10);
      setDonatePostageRevenue(true);
      setDonateAdClickRevenue(true);
    }
    setErrorMessage(null);
  }, [preferencesData]);

  const handleDelete = useCallback(() => {
    if (window.confirm('Are you sure you want to stop donating?')) {
      deleteMutation.mutate();
    }
  }, [deleteMutation]);

  const hasChanges =
    selectedCharity !== null ||
    (preferencesData?.hasPreference &&
      (donationPercentage !== preferencesData.preference?.DonationPercentage ||
        donatePostageRevenue !== preferencesData.preference?.DonatePostageRevenue ||
        donateAdClickRevenue !== preferencesData.preference?.DonateAdClickRevenue));

  const formatDate = (dateStr: string | null) => fmtDate(dateStr);
  const formatPeriod = (start: string, end: string) => `${fmtMonthYear(start)} – ${fmtMonthYear(end)}`;

  if (preferencesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: CHARITY_THEME.primary }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: CHARITY_THEME.primary }}>
          <DonationIcon fontSize="large" />
          Donation Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Donate a percentage of your postage and ad revenue to verified charities. Your contributions are processed monthly.
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* Current Donation Card */}
      <Card
        sx={{
          mb: 3,
          background: preferencesData?.hasPreference ? CHARITY_THEME.gradient : 'grey.100',
          color: preferencesData?.hasPreference ? 'white' : 'text.primary',
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DonationIcon />
            Current Donation
          </Typography>
          {preferencesData?.hasPreference && preferencesData.preference ? (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {preferencesData.preference.CharityName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
                @{preferencesData.preference.CharityUsername}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95 }}>
                {preferencesData.preference.DonationPercentage}% of selected revenue
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PostageIcon fontSize="small" />
                  <Typography variant="body2">
                    Postage revenue: {preferencesData.preference.DonatePostageRevenue ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AdClickIcon fontSize="small" />
                  <Typography variant="body2">
                    Ad click revenue: {preferencesData.preference.DonateAdClickRevenue ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
                Next processing: {formatDate(preferencesData.preference.NextProcessingDate)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1">You are not currently donating to a charity.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit / Set Donation Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: CHARITY_THEME.primary }}>
            <SearchIcon />
            {preferencesData?.hasPreference ? 'Change Charity or Settings' : 'Search and Select Charity'}
          </Typography>

          <Autocomplete
            options={charities}
            getOptionLabel={(option) => option.OrganizationName || option.Username || ''}
            value={selectedCharity}
            inputValue={charitySearch}
            onInputChange={(_, value) => setCharitySearch(value)}
            onChange={(_, newValue) => setSelectedCharity(newValue)}
            loading={charitiesLoading}
            filterOptions={(x) => x}
            isOptionEqualToValue={(option, value) => option.CredentialId === value?.CredentialId}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.CredentialId}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                    {option.OrganizationName || option.Username}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      @{option.Username}
                    </Typography>
                    {(option.City || option.State) && (
                      <Typography variant="caption" color="text.secondary">
                        · {[option.City, option.State].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search verified charities"
                placeholder="Search by organization name or username..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {charitiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: selectedCharity ? 1 : 3, maxWidth: 500 }}
          />

          {/* Selected charity confirmation — shows both org name and username */}
          {selectedCharity && (
            <Box
              sx={{
                mb: 3,
                maxWidth: 500,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.200',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: CHARITY_THEME.primary, mt: 0.25, flexShrink: 0 }} fontSize="small" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: CHARITY_THEME.primary }}>
                  {selectedCharity.OrganizationName || selectedCharity.Username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Username: @{selectedCharity.Username}
                  {(selectedCharity.City || selectedCharity.State) && (
                    <> · {[selectedCharity.City, selectedCharity.State].filter(Boolean).join(', ')}</>
                  )}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Donation percentage
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Slider
              value={donationPercentage}
              onChange={(_, value) => setDonationPercentage(value as number)}
              min={1}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ flex: 1, maxWidth: 400, color: CHARITY_THEME.primary }}
            />
            <Typography variant="body1" fontWeight="bold" sx={{ minWidth: 48 }}>
              {donationPercentage}%
            </Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Donation sources
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={donatePostageRevenue}
                  onChange={(e) => setDonatePostageRevenue(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: CHARITY_THEME.primary } }}
                />
              }
              label="Donate a percentage of postage revenue"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={donateAdClickRevenue}
                  onChange={(e) => setDonateAdClickRevenue(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: CHARITY_THEME.primary } }}
                />
              }
              label="Donate a percentage of ad click revenue"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveMutation.isPending || !hasChanges}
              sx={{ bgcolor: CHARITY_THEME.primary, '&:hover': { bgcolor: CHARITY_THEME.light } }}
            >
              {saveMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={!hasChanges}>
              Cancel
            </Button>
            {preferencesData?.hasPreference && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                Stop Donating
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Donation History */}
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: CHARITY_THEME.primary, mb: 2 }}>
        <DonationIcon fontSize="small" />
        Donation History
      </Typography>
      {historyData && historyData.totalDonated > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Total donated to date: <strong>${historyData.totalDonated.toFixed(2)}</strong>
        </Alert>
      )}

      <TableContainer component={Paper}>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: CHARITY_THEME.primary }} />
          </Box>
        ) : !historyData?.donations?.length ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No donation history yet.</Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Charity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Postage</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ad Clicks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Processed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.donations.map((d) => (
                  <TableRow key={d.DonationId} hover>
                    <TableCell>{d.CharityName}</TableCell>
                    <TableCell>{formatPeriod(d.PeriodStart, d.PeriodEnd)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: CHARITY_THEME.primary }}>
                      ${d.Amount.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">${d.PostageAmount.toFixed(2)}</TableCell>
                    <TableCell align="right">${d.AdClickAmount.toFixed(2)}</TableCell>
                    <TableCell>{d.Status}</TableCell>
                    <TableCell>{formatDate(d.ProcessedDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {historyData.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={historyData.totalPages}
                  page={historyPage}
                  onChange={(_, p) => setHistoryPage(p)}
                  color="primary"
                  sx={{ '& .MuiPaginationItem-root': { color: CHARITY_THEME.primary } }}
                />
              </Box>
            )}
          </>
        )}
      </TableContainer>
    </Container>
  );
}
